import type { StandaloneMenuItem } from './menu'

/** 商城独立窗经营视角：对齐产品中心 platform / self */
export type MallUiScope = 'platform' | 'self'

export const MALL_UI_SCOPE_STORAGE_KEY = 'mall_ui_scope'

export const MALL_MODE_OPTIONS: Array<{ label: string; value: MallUiScope }> = [
  { label: '平台', value: 'platform' },
  { label: '自营', value: 'self' }
]

/**
 * 仅平台视角可见（自营隐藏）。
 * 供应商 / 货池 / 供货结算等外发能力为 SHARED，不得写入本表。
 * 「店铺管理」= 多店入驻/列表运营，自营视角整组隐藏（一家店不需要管多店）。
 */
const MALL_PLATFORM_ONLY_PATH_MARKERS = [
  '/mall/apply',
  'merchant-apply',
  '/mall/shop-manager',
  '/mall/merchant/',
  'shop-manager',
  // 入驻套餐：/mall/merchant/merchant-plan
  'merchant-plan'
]

const MALL_PLATFORM_ONLY_TITLE_MARKERS = [
  '入驻',
  '店铺管理',
  '商家管理',
  '商家中心',
  '店铺与商家'
]

export function isMallUiScope(value: unknown): value is MallUiScope {
  return value === 'platform' || value === 'self'
}

export function readMallUiScope(): MallUiScope | null {
  try {
    const raw = localStorage.getItem(MALL_UI_SCOPE_STORAGE_KEY)
    return isMallUiScope(raw) ? raw : null
  } catch {
    return null
  }
}

export function writeMallUiScope(scope: MallUiScope) {
  try {
    localStorage.setItem(MALL_UI_SCOPE_STORAGE_KEY, scope)
  } catch {
    /* ignore */
  }
}

const pathLooksPlatformOnly = (path: string) => {
  const normalized = path.split('?')[0].toLowerCase()
  // 店铺形象挂商家中心后 path 含 /mall/merchant/，但属自营叶子，不得当平台专属裁掉
  if (normalized.includes('shop-profile')) {
    return false
  }
  return MALL_PLATFORM_ONLY_PATH_MARKERS.some((marker) =>
    normalized.includes(marker.toLowerCase())
  )
}

const titleLooksPlatformOnly = (title: string) =>
  MALL_PLATFORM_ONLY_TITLE_MARKERS.some((marker) => title.includes(marker))

/** 当前 path 是否属自营视角应拦截的平台专属页（含入驻详情、店铺管理） */
export function isMallPlatformOnlyPath(path: string): boolean {
  return pathLooksPlatformOnly(path)
}

/**
 * 自营视角侧栏软注入：店铺形象（正式挂商家中心 9003700，path=/mall/merchant/shop-profile）
 */
export const MALL_SELF_SHOP_PROFILE_PATH_SUFFIX = '/merchant/shop-profile'

/** @param mallBase `/mall` 或 `/ai-Application-center/mall` */
export function buildMallSelfShopProfileMenu(mallBase = '/mall'): StandaloneMenuItem {
  const base = mallBase.replace(/\/+$/, '') || '/mall'
  return {
    key: 'mall-self-shop-profile',
    title: '店铺形象',
    path: `${base}${MALL_SELF_SHOP_PROFILE_PATH_SUFFIX}`,
    icon: 'ep:shop'
  }
}

/** 把叶子挂到指定标题的一级目录下；找不到则返回 null（由调用方决定顶层兜底） */
function nestLeafUnderTitle(
  items: StandaloneMenuItem[],
  parentTitle: string,
  leaf: StandaloneMenuItem
): StandaloneMenuItem[] | null {
  const idx = items.findIndex((item) => item.title === parentTitle)
  if (idx < 0) return null
  const parent = items[idx]
  const children = [...(parent.children || [])]
  if (children.some((c) => c.title === leaf.title || normalizePath(c.path) === normalizePath(leaf.path))) {
    return items
  }
  children.push(leaf)
  const next = [...items]
  next[idx] = { ...parent, children }
  return next
}

function isRechargePackageMenu(item: StandaloneMenuItem): boolean {
  if (item.title === '充值套餐') return true
  return normalizePath(item.path).toLowerCase().includes('wallet-recharge-package')
}

/** 从树中摘掉匹配的叶子（保留目录壳），返回摘出项 */
function extractMatchingLeaves(
  items: StandaloneMenuItem[],
  match: (item: StandaloneMenuItem) => boolean
): { tree: StandaloneMenuItem[]; extracted: StandaloneMenuItem | null } {
  let extracted: StandaloneMenuItem | null = null
  const walk = (list: StandaloneMenuItem[]): StandaloneMenuItem[] => {
    const out: StandaloneMenuItem[] = []
    for (const item of list) {
      if (match(item) && !item.children?.length) {
        if (!extracted) extracted = { ...item }
        continue
      }
      if (item.children?.length) {
        out.push({ ...item, children: walk(item.children) })
        continue
      }
      out.push(item)
    }
    return out
  }
  return { tree: walk(items), extracted }
}

function nestLeafUnderTradeParents(
  items: StandaloneMenuItem[],
  leaf: StandaloneMenuItem
): StandaloneMenuItem[] | null {
  for (const title of ['交易管理', '交易中心', '订单中心']) {
    const nested = nestLeafUnderTitle(items, title, leaf)
    if (nested) return nested
  }
  // 标题未对齐时按 path 认交易目录
  const idx = items.findIndex((item) => {
    const p = normalizePath(item.path).toLowerCase()
    return p.endsWith('/trade') || p.includes('/mall/trade')
  })
  if (idx < 0) return null
  const parent = items[idx]
  const children = [...(parent.children || [])]
  if (
    children.some(
      (c) => c.title === leaf.title || normalizePath(c.path) === normalizePath(leaf.path)
    )
  ) {
    return items
  }
  children.push(leaf)
  const next = [...items]
  next[idx] = { ...parent, children }
  return next
}

/**
 * 侧栏软注入「用户管理」：库里既没有「用户管理」也没有「会员管理」时补一条入口。
 * 正式菜单以 SQL 9002500 树为准；本注入仅防独立窗空白。
 */
export function buildMallUserManageMenu(mallBase = '/mall'): StandaloneMenuItem {
  const base = mallBase.replace(/\/+$/, '') || '/mall'
  return {
    key: 'mall-user-manage',
    title: '会员管理',
    path: `${base}/member/user`,
    icon: 'ep:user-filled'
  }
}

function treeHasUserManage(items: StandaloneMenuItem[]): boolean {
  return items.some((item) => {
    if (
      item.title === '用户管理' ||
      item.title === '会员管理' ||
      item.title === '会员中心'
    ) {
      return true
    }
    const path = normalizePath(item.path).toLowerCase()
    if (path.includes('/mall/member') || path.endsWith('/member') || path.includes('/member/user')) {
      return true
    }
    return Boolean(item.children?.length && treeHasUserManage(item.children))
  })
}

export function appendMallUserManageMenu(
  items: StandaloneMenuItem[],
  mallBase = '/mall'
): StandaloneMenuItem[] {
  if (treeHasUserManage(items)) {
    return items
  }
  const userMenu = buildMallUserManageMenu(mallBase)
  const next = [...items]
  const overviewIdx = items.findIndex((item) => {
    const p = normalizePath(item.path)
    return p.includes('/mall/overview') || item.title === '概况'
  })
  // 插在概况后、店铺形象附近，与产品一级模块并列
  const insertAt = overviewIdx >= 0 ? overviewIdx + 1 : 0
  next.splice(insertAt, 0, userMenu)
  return next
}

/** 充值套餐正式 path（挂交易管理后） */
export function buildMallRechargePackageMenu(mallBase = '/mall'): StandaloneMenuItem {
  const base = mallBase.replace(/\/+$/, '') || '/mall'
  return {
    key: 'mall-wallet-recharge-package',
    title: '充值套餐',
    path: `${base}/trade/wallet-recharge-package`,
    icon: 'ep:wallet'
  }
}

/**
 * 强制「充值套餐」挂到交易管理下：
 * - 库里仍挂根/会员时会从一级摘掉再塞进交易二级
 * - 库里没有则软注入一条
 * 解决：旧逻辑见树里已有标题就跳过，导致一直停在概况下面。
 */
export function appendMallRechargePackageMenu(
  items: StandaloneMenuItem[],
  mallBase = '/mall'
): StandaloneMenuItem[] {
  const template = buildMallRechargePackageMenu(mallBase)
  const { tree, extracted } = extractMatchingLeaves(items, isRechargePackageMenu)
  const leaf: StandaloneMenuItem = extracted
    ? {
        ...extracted,
        title: '充值套餐',
        path: template.path,
        icon: extracted.icon || template.icon
      }
    : template

  const nested = nestLeafUnderTradeParents(tree, leaf)
  if (nested) return nested

  // 尚无交易一级时才顶栏兜底（避免入口丢失）
  const next = [...tree]
  const overviewIdx = tree.findIndex((item) => {
    const p = normalizePath(item.path)
    return p.includes('/mall/overview') || item.title === '概况'
  })
  const insertAt = overviewIdx >= 0 ? overviewIdx + 1 : 0
  next.splice(insertAt, 0, leaf)
  return next
}

/** 侧栏软注入「充值记录」：库里没有同名/同 path 时补一条（正式菜单以 SQL 9002710 为准） */
export function buildMallWalletRechargeMenu(mallBase = '/mall'): StandaloneMenuItem {
  const base = mallBase.replace(/\/+$/, '') || '/mall'
  return {
    key: 'mall-wallet-recharge',
    title: '充值记录',
    // 正式菜单挂会员中心后 path 为 /mall/member/wallet-recharge
    path: `${base}/member/wallet-recharge`,
    icon: 'ep:document'
  }
}

function treeHasWalletRecharge(items: StandaloneMenuItem[]): boolean {
  return items.some((item) => {
    if (item.title === '充值记录') return true
    const path = normalizePath(item.path).toLowerCase()
    // 避免与「充值套餐」path 误判：只匹配 .../wallet-recharge 结尾，不含 -package
    if (/(^|\/)wallet-recharge$/.test(path)) return true
    return Boolean(item.children?.length && treeHasWalletRecharge(item.children))
  })
}

export function appendMallWalletRechargeMenu(
  items: StandaloneMenuItem[],
  mallBase = '/mall'
): StandaloneMenuItem[] {
  if (treeHasWalletRecharge(items)) {
    return items
  }
  const menu = buildMallWalletRechargeMenu(mallBase)
  const next = [...items]
  // 优先插在「充值套餐」后，否则概况后
  const packageIdx = items.findIndex((item) => {
    const p = normalizePath(item.path).toLowerCase()
    return item.title === '充值套餐' || p.includes('wallet-recharge-package')
  })
  if (packageIdx >= 0) {
    next.splice(packageIdx + 1, 0, menu)
    return next
  }
  const overviewIdx = items.findIndex((item) => {
    const p = normalizePath(item.path)
    return p.includes('/mall/overview') || item.title === '概况'
  })
  const insertAt = overviewIdx >= 0 ? overviewIdx + 1 : 0
  next.splice(insertAt, 0, menu)
  return next
}

export function appendMallSelfOnlyMenus(
  items: StandaloneMenuItem[],
  scope: MallUiScope,
  mallBase = '/mall'
): StandaloneMenuItem[] {
  if (scope !== 'self') {
    return items
  }
  const profile = buildMallSelfShopProfileMenu(mallBase)
  const profilePath = normalizePath(profile.path)
  const exists =
    items.some((item) => pathExistsInTree(item, profilePath)) ||
    items.some((item) => pathExistsInTree(item, `${mallBase.replace(/\/+$/, '') || '/mall'}/shop-profile`))
  if (exists) {
    return items
  }
  // 优先挂到商家管理下（与正式菜单一致）；无该目录时顶栏兜底
  const nested =
    nestLeafUnderTitle(items, '商家管理', profile) ||
    nestLeafUnderTitle(items, '商家中心', profile)
  if (nested) return nested
  const next = [...items]
  const overviewIdx = items.findIndex((item) => {
    const p = normalizePath(item.path)
    return p.includes('/mall/overview') || item.title === '概况'
  })
  if (overviewIdx >= 0) {
    next.splice(overviewIdx + 1, 0, profile)
  } else {
    next.unshift(profile)
  }
  return next
}

function normalizePath(path: string) {
  return path.split('?')[0].replace(/\/+$/, '') || '/'
}

function pathExistsInTree(item: StandaloneMenuItem, target: string): boolean {
  if (normalizePath(item.path) === target) return true
  return Boolean(item.children?.some((child) => pathExistsInTree(child, target)))
}

/** 叶子或节点是否属「仅平台」 */
export function isMallPlatformOnlyMenu(item: StandaloneMenuItem): boolean {
  if (pathLooksPlatformOnly(item.path) || titleLooksPlatformOnly(item.title)) {
    return true
  }
  if (!item.children?.length) {
    return false
  }
  // 子节点全是平台专属 → 父也视为平台专属（便于整组裁掉）
  return item.children.every((child) => isMallPlatformOnlyMenu(child))
}

/**
 * 按商城经营视角过滤独立窗侧栏。
 * - platform：全量（含入驻）
 * - self：隐藏入驻等平台专属；供应商/货池等保留
 */
export function filterMallMenuByScope(
  items: StandaloneMenuItem[],
  scope: MallUiScope
): StandaloneMenuItem[] {
  if (scope === 'platform') {
    return items
  }
  return items.flatMap((item) => {
    if (isMallPlatformOnlyMenu(item) && !item.children?.length) {
      return []
    }
    const children = item.children?.length
      ? filterMallMenuByScope(item.children, scope)
      : undefined
    if (isMallPlatformOnlyMenu(item) && !children?.length) {
      return []
    }
    // 父节点本身带入驻标题但还有共享子节点时：保留父、只留过滤后的子
    if (titleLooksPlatformOnly(item.title) && !pathLooksPlatformOnly(item.path)) {
      if (!children?.length) return []
      return [{ ...item, children }]
    }
    if (pathLooksPlatformOnly(item.path) && !children?.length) {
      return []
    }
    return [
      {
        ...item,
        children: children?.length ? children : undefined
      }
    ]
  })
}

/** 切视角后优先落点 */
export function pickMallScopeLandingPath(leaves: StandaloneMenuItem[]): string | undefined {
  const overview = leaves.find((item) => {
    const path = item.path.split('?')[0]
    return path.includes('/mall/overview') || item.title === '概况'
  })
  return overview?.path || leaves[0]?.path
}

/** 商城总裁驾驶舱：新窗口纯净全屏（不带独立窗外壳） */
export const MALL_CEO_COCKPIT_VIEW_PATH = '/mall/cockpit-view'
export const MALL_CEO_COCKPIT_VIEW_NAME = 'MallCockpitView'

const MALL_CEO_COCKPIT_TITLES = ['总裁驾驶舱', '商城首页']

/** 侧栏是否命中商城总裁驾驶舱（按标题或 /mall/home 路径） */
export function isMallCeoCockpitMenu(item: Pick<StandaloneMenuItem, 'title' | 'path'>): boolean {
  if (MALL_CEO_COCKPIT_TITLES.includes(item.title)) {
    return true
  }
  const path = normalizePath(item.path).toLowerCase()
  return path === '/mall/home' || path.endsWith('/mall/home')
}
